import { useState } from "react";
import axios from "axios";
import { ArrowRight, GenderMale, GenderFemale, Envelope, Target, Sparkle, CheckCircle, Warning } from "@phosphor-icons/react";
import EmailLinkPanel from "@/components/EmailLinkPanel";
import { useWeightUnit } from "@/lib/units";
import { PRIMARY_GOALS, findGoal } from "@/lib/goals";

const GENDER_OPTIONS = [
  { value: "male", label: "Male", icon: GenderMale },
  { value: "female", label: "Female", icon: GenderFemale },
];

export default function Onboarding({ api, onComplete }) {
  const { unit: weightUnit, toKg, label: weightLabel } = useWeightUnit();
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [primaryGoal, setPrimaryGoal] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [profileCreated, setProfileCreated] = useState(false);
  const [emailLinked, setEmailLinked] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // ── "Magic moment": kick off an AI-generated starter plan in the background
  // the instant the user picks their goal. By the time they reach step 6 (or
  // tap Done) the plan is usually ready and waiting for them on the dashboard.
  const [planGenerating, setPlanGenerating] = useState(false);
  const [planResult, setPlanResult] = useState(null);
  const [planError, setPlanError] = useState("");
  const [planFired, setPlanFired] = useState(false);

  const kickoffAIPlan = async (goalValue) => {
    if (planFired) return;
    setPlanFired(true);
    setPlanGenerating(true);
    setPlanError("");
    const focus = findGoal(goalValue)?.aiFocus || "General Fitness";
    try {
      const res = await axios.post(`${api}/plans/ai-generate`, {
        goal_focus: focus,
        weeks_count: 4,
        days_per_week: 4,
        equipment: "Gym",
        notes: "",
        cycle_mode: false,
        experience: "beginner",
      });
      setPlanResult(res.data);
      // Stash a tiny payload for the Dashboard's welcome hero card.
      try {
        localStorage.setItem("uptop-welcome-plan", JSON.stringify({
          id: res.data?.id,
          name: res.data?.name,
          weeks_count: res.data?.weeks_count,
          days_per_week: res.data?.days_per_week,
          goal_focus: focus,
        }));
      } catch (e) { /* ignore */ }
    } catch (e) {
      setPlanError("Couldn't auto-generate a plan. You can build one anytime in Plans.");
    } finally {
      setPlanGenerating(false);
    }
  };

  const handleRestoreLegacy = async () => {
    setError("");
    setRestoring(true);
    try {
      const res = await axios.post(`${api}/account/claim-legacy`);
      if (res.data?.claimed) {
        onComplete();
      } else {
        setError(
          res.data?.reason === "no legacy data"
            ? "No legacy data found. Continue onboarding instead."
            : "Couldn't restore data on this device."
        );
      }
    } catch (e) {
      setError("Couldn't reach the server. Try again.");
    } finally {
      setRestoring(false);
    }
  };

  const canProceed = () => {
    if (step === 0) return name.trim().length > 0;
    if (step === 1) return gender !== "";
    if (step === 2) return age !== "" && parseInt(age) > 0;
    if (step === 3) return weight !== "" && parseFloat(weight) > 0;
    if (step === 4) return height !== "" && parseFloat(height) > 0;
    if (step === 5) return primaryGoal !== "";
    if (step === 6) return true; // email step is optional
    return false;
  };

  const saveProfile = async () => {
    setError("");
    setSaving(true);
    try {
      await axios.post(`${api}/profile`, {
        name: name.trim(),
        gender,
        age: parseInt(age),
        weight: parseFloat(toKg(parseFloat(weight)).toFixed(2)),
        height: parseFloat(height),
        primary_goal: primaryGoal,
      });
      setProfileCreated(true);
      return true;
    } catch (e) {
      setError("Failed to save profile. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    setError("");
    if (step < 5) {
      // Lazily save profile right after height (step 4) so the email step has a profile to attach to.
      if (step === 4 && !profileCreated) {
        // We only have height locally; defer saving to after goal is chosen on step 5.
      }
      setStep(step + 1);
      return;
    }
    if (step === 5) {
      if (!profileCreated) {
        const ok = await saveProfile();
        if (!ok) return;
      }
      // Fire AI plan generation in the background — don't await, don't block.
      kickoffAIPlan(primaryGoal);
      setStep(6);
      return;
    }
    // step === 6 → finish (with or without email)
    onComplete();
  };

  const handleSkipEmail = () => {
    onComplete();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && canProceed()) handleNext();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#0F0F0F] flex items-center justify-center" data-testid="onboarding-screen">
      <div className="w-full max-w-md px-6">
        {/* Logo */}
        <div className="text-center mb-10">
          <h1
            className="text-4xl sm:text-5xl font-black tracking-tighter uppercase text-white"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            data-testid="onboarding-logo"
          >
            <span className="text-[#E05A1D]">UP</span>TOP
          </h1>
          <p className="text-sm text-[#71717A] mt-2">Let's set up your profile</p>
        </div>

        {/* Progress */}
        <div className="flex gap-2 mb-8" data-testid="onboarding-progress">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className={`h-1 flex-1 transition-all duration-300 ${
                i <= step ? "bg-[#E05A1D]" : "bg-[#27272A]"
              }`}
            />
          ))}
        </div>

        {/* Step 0: Name */}
        {step === 0 && (
          <div className="space-y-6" data-testid="onboarding-step-name">
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              What's your name?
            </h2>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full bg-[#161616] border border-[#27272A] text-white text-3xl font-bold text-center py-6 focus:border-[#E05A1D] focus:ring-1 focus:ring-[#E05A1D] outline-none transition-all"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              placeholder="Your name"
              maxLength={40}
              autoFocus
              data-testid="name-input"
            />
            <div className="pt-2">
              <button
                type="button"
                onClick={handleRestoreLegacy}
                disabled={restoring}
                className="w-full text-xs text-[#71717A] hover:text-[#E05A1D] font-semibold uppercase tracking-wider transition-colors disabled:opacity-40"
                data-testid="restore-legacy-btn"
              >
                {restoring ? "Restoring…" : "Already using UpTop? Restore my data →"}
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Gender */}
        {step === 1 && (
          <div className="space-y-6" data-testid="onboarding-step-gender">
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              What's your gender?
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {GENDER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setGender(opt.value)}
                  className={`border p-5 flex flex-col items-center gap-3 transition-all ${
                    gender === opt.value
                      ? "border-[#E05A1D] bg-[#E05A1D]/10 text-[#E05A1D]"
                      : "border-[#27272A] bg-[#161616] text-[#A1A1AA] hover:border-[#3f3f46] hover:text-white"
                  }`}
                  data-testid={`gender-${opt.value}`}
                >
                  <opt.icon size={32} weight={gender === opt.value ? "fill" : "regular"} />
                  <span className="text-xs font-bold uppercase tracking-wider">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Age */}
        {step === 2 && (
          <div className="space-y-6" data-testid="onboarding-step-age">
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              How old are you?
            </h2>
            <div className="relative">
              <input
                type="number"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full bg-[#161616] border border-[#27272A] text-white text-4xl font-black text-center py-6 focus:border-[#E05A1D] focus:ring-1 focus:ring-[#E05A1D] outline-none transition-all"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                placeholder="25"
                min="1"
                max="100"
                autoFocus
                data-testid="age-input"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#71717A] text-sm font-bold uppercase">years</span>
            </div>
          </div>
        )}

        {/* Step 3: Weight */}
        {step === 3 && (
          <div className="space-y-6" data-testid="onboarding-step-weight">
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              What's your weight?
            </h2>
            <div className="relative">
              <input
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full bg-[#161616] border border-[#27272A] text-white text-4xl font-black text-center py-6 focus:border-[#E05A1D] focus:ring-1 focus:ring-[#E05A1D] outline-none transition-all"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                placeholder={weightUnit === "lb" ? "165" : "75"}
                step="0.1"
                min={weightUnit === "lb" ? "44" : "20"}
                max={weightUnit === "lb" ? "660" : "300"}
                autoFocus
                data-testid="weight-input"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#71717A] text-sm font-bold uppercase">{weightLabel}</span>
            </div>
          </div>
        )}

        {/* Step 4: Height */}
        {step === 4 && (
          <div className="space-y-6" data-testid="onboarding-step-height">
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              How tall are you?
            </h2>
            <div className="relative">
              <input
                type="number"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full bg-[#161616] border border-[#27272A] text-white text-4xl font-black text-center py-6 focus:border-[#E05A1D] focus:ring-1 focus:ring-[#E05A1D] outline-none transition-all"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                placeholder="175"
                step="0.1"
                min="120"
                max="230"
                autoFocus
                data-testid="height-input"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#71717A] text-sm font-bold uppercase">cm</span>
            </div>
          </div>
        )}

        {/* Step 5: Primary Goal */}
        {step === 5 && (
          <div className="space-y-5" data-testid="onboarding-step-goal">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Target size={22} weight="bold" className="text-[#E05A1D]" />
                <h2
                  className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  What's your goal?
                </h2>
              </div>
              <p className="text-sm text-[#71717A]">Pick one — we'll tailor recommendations to it. You can change this anytime in Settings.</p>
            </div>
            <div className="space-y-2">
              {PRIMARY_GOALS.map((g) => {
                const Icon = g.icon;
                const active = primaryGoal === g.value;
                return (
                  <button
                    key={g.value}
                    onClick={() => setPrimaryGoal(g.value)}
                    className={`w-full border p-4 flex items-center gap-4 transition-all text-left ${
                      active
                        ? "border-[#E05A1D] bg-[#E05A1D]/10"
                        : "border-[#27272A] bg-[#161616] hover:border-[#3f3f46]"
                    }`}
                    data-testid={`goal-option-${g.value}`}
                  >
                    <div className={`w-10 h-10 flex items-center justify-center shrink-0 ${
                      active ? "bg-[#E05A1D]/20 text-[#E05A1D]" : "bg-[#0F0F0F] text-[#A1A1AA]"
                    }`}>
                      <Icon size={20} weight="bold" />
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-bold uppercase tracking-wide ${active ? "text-white" : "text-white"}`}>{g.label}</p>
                      <p className="text-xs text-[#71717A] mt-0.5">{g.headline}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 6: Optional email link */}
        {step === 6 && (
          <div className="space-y-5" data-testid="onboarding-step-email">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Envelope size={22} weight="bold" className="text-[#E05A1D]" />
                <h2
                  className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Link your email?
                </h2>
              </div>
              <p className="text-sm text-[#71717A]">
                Optional. Get a weekly recap of your training and unlock syncing later. You can do this anytime in Settings.
              </p>
            </div>

            {/* Magic moment: AI plan generated in the background */}
            {(planGenerating || planResult || planError) && (
              <div
                className="bg-gradient-to-br from-[#E05A1D]/10 to-transparent border border-[#E05A1D]/30 p-4 flex items-start gap-3"
                data-testid="onboarding-plan-status"
              >
                {planGenerating && (
                  <>
                    <div className="w-9 h-9 flex items-center justify-center border border-[#E05A1D]/40 text-[#E05A1D] shrink-0 relative">
                      <Sparkle size={16} weight="fill" className="opacity-50" />
                      <div className="absolute inset-0 border-2 border-[#E05A1D] border-t-transparent rounded-full animate-spin" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold uppercase tracking-wide text-white">Crafting your first plan</p>
                      <p className="text-xs text-[#A1A1AA] mt-0.5">
                        UpTop's AI coach is designing a 4-week {findGoal(primaryGoal)?.aiFocus.toLowerCase() || "training"} program tailored to your profile.
                      </p>
                    </div>
                  </>
                )}
                {planResult && !planGenerating && (
                  <>
                    <div className="w-9 h-9 flex items-center justify-center bg-[#22c55e]/15 text-[#22c55e] shrink-0">
                      <CheckCircle size={18} weight="fill" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold uppercase tracking-wide text-white" data-testid="onboarding-plan-ready">
                        Your first plan is ready
                      </p>
                      <p className="text-xs text-[#A1A1AA] mt-0.5 truncate">{planResult.name}</p>
                    </div>
                  </>
                )}
                {planError && !planGenerating && !planResult && (
                  <>
                    <div className="w-9 h-9 flex items-center justify-center bg-amber-500/15 text-amber-400 shrink-0">
                      <Warning size={18} weight="fill" />
                    </div>
                    <p className="text-xs text-[#A1A1AA] leading-relaxed">{planError}</p>
                  </>
                )}
              </div>
            )}

            <EmailLinkPanel
              api={api}
              compact
              onLinked={() => setEmailLinked(true)}
            />
          </div>
        )}

        {/* Error */}
        {error && (
          <p className="text-sm text-red-500 mt-3" data-testid="onboarding-error">{error}</p>
        )}

        {/* Continue button */}
        {step !== 6 && (
          <button
            onClick={handleNext}
            disabled={!canProceed() || saving}
            className="w-full mt-8 bg-[#E05A1D] hover:bg-[#F06B2E] disabled:opacity-30 disabled:hover:bg-[#E05A1D] text-white font-bold uppercase tracking-wide py-4 flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
            data-testid="onboarding-next-btn"
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                Continue
                <ArrowRight size={18} weight="bold" />
              </>
            )}
          </button>
        )}

        {/* Step 6 actions: Finish or Skip */}
        {step === 6 && (
          <div className="mt-8 space-y-3">
            <button
              onClick={onComplete}
              className="w-full bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide py-4 flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
              data-testid="onboarding-finish-btn"
            >
              {emailLinked ? "Get Started" : "Finish Without Email"}
              <ArrowRight size={18} weight="bold" />
            </button>
            {!emailLinked && (
              <button
                onClick={handleSkipEmail}
                className="w-full text-sm text-[#71717A] hover:text-white font-semibold uppercase tracking-wider transition-colors"
                data-testid="onboarding-skip-email-btn"
              >
                Skip for now
              </button>
            )}
          </div>
        )}

        {/* Skip back */}
        {step > 0 && step !== 6 && (
          <button
            onClick={() => setStep(step - 1)}
            className="w-full mt-3 text-sm text-[#71717A] hover:text-white font-semibold uppercase tracking-wider transition-colors"
            data-testid="onboarding-back-btn"
          >
            Back
          </button>
        )}
      </div>
    </div>
  );
}
